import { Box, CircularProgress, Typography } from "@mui/material";
import { authenticationApi } from "apis";
import { auth } from "config/firebase.config";
import { SecureStorageKeys } from "constants/secureStorage";
import { ChatSocketProvider } from "contexts/ChatSocketContext";
import { NotificationProvider } from "contexts/NotificationContext";
import { signInWithCustomToken } from "firebase/auth";
import { GoogleSignInRequestUserTypeEnum } from "libs/cellera-sdk";
import React, { Suspense, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { secureStorage } from "utils/secureStorage";

import { EPath } from "../constants/path";
import { SubscriptionStatus } from "../constants/payment";
import { useAuth } from "../contexts/AuthContext";
import { usePaymentStatus } from "../hooks/usePaymentStatus";
import { RouteConfig } from "./routeConfig";

interface ProtectedRouteProps {
  route: RouteConfig;
}

/**
 * Loading fallback for lazy-loaded route components
 */
const RouteLoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: 2,
      backgroundColor: "#f5f5f5",
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ route }) => {
  const { user, userClaims, loading } = useAuth();
  const location = useLocation();
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const Element = route.element;

  // Fetch subscription status to verify Stripe payment success
  const { data: subscriptionStatus, isLoading: isLoadingPayment } =
    usePaymentStatus(!!userClaims?.isPracticeOwner);
  const isPaymentActive =
    subscriptionStatus?.status === SubscriptionStatus.ACTIVE;

  // Auto-refresh userClaims when subscription is active but user still needs onboarding
  // This handles the case when payment succeeds and requiresOnboarding should become false
  const refreshUserTokenAndClaims = async () => {
    try {
      setIsRefreshingToken(true);
      const currentUser = auth?.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken(true);

      const response = await authenticationApi.googleSignIn({
        googleSignInRequest: {
          idToken,
          userType: GoogleSignInRequestUserTypeEnum.Provider,
          platform: "web",
        },
      });

      // set custom token to local storage
      if (response.customToken) {
        const userCred = await signInWithCustomToken(
          auth,
          response.customToken
        );
        const idToken = await userCred.user.getIdToken();
        secureStorage.set(SecureStorageKeys.CUSTOM_TOKEN, idToken);
        window.location.reload();
      }
    } catch (error: unknown) {
      console.error("Error refreshing token: ", error);
    } finally {
      setIsRefreshingToken(false);
    }
  };

  useEffect(() => {
    const processRefreshUserTokenAndClaims = async () => {
      if (isPaymentActive && userClaims?.requiresOnboarding)
        await refreshUserTokenAndClaims();
    };
    void processRefreshUserTokenAndClaims();
  }, [isPaymentActive, userClaims?.requiresOnboarding]);

  // Show loading while checking authentication or payment status
  if (loading || isLoadingPayment || isRefreshingToken) {
    return <RouteLoadingFallback />;
  }

  // If route is not protected, render directly with Suspense for lazy components
  if (!route.protected) {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Element />
      </Suspense>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={EPath.LOGIN} state={{ from: location }} replace />;
  }

  if (location.pathname === EPath.PAYMENT_SUCCESSFUL) {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Element />
      </Suspense>
    );
  }

  if (isPaymentActive && userClaims?.isAwaitingProtocolDeployment === true) {
    return <Navigate to={EPath.PAYMENT_SUCCESSFUL} replace />;
  }

  // Handle onboarding route
  if (
    route.requireOnboarding &&
    !userClaims?.requiresOnboarding &&
    isPaymentActive
  ) {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Element />
      </Suspense>
    );
  }

  if (
    !route.requireOnboarding &&
    userClaims?.requiresOnboarding === true &&
    !isPaymentActive
  ) {
    return <Navigate to={EPath.ONBOARDING} replace />;
  }

  // User is authenticated and has completed onboarding (or onboarding is not required)
  return (
    <ChatSocketProvider>
      <NotificationProvider>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Element />
        </Suspense>
      </NotificationProvider>
    </ChatSocketProvider>
  );
};

export default ProtectedRoute;
